/**
 * @fileoverview Shared click-id → channel mapping (edge + client safe).
 * @description Single source of truth for bare click-id classification used by
 * client attribution and edge middleware cookie stamping.
 *
 * @module lib/analytics/click-id-channel
 */

/**
 * The channel a bare click id implies when no `utm_*` disambiguates it.
 * Insertion order is lookup priority (Google first). Keys are lowercase.
 */
export const CLICK_ID_CHANNEL: Readonly<
  Record<string, { readonly source: string; readonly medium: string }>
> = {
  gclid: { source: "google", medium: "cpc" },
  gbraid: { source: "google", medium: "cpc" },
  wbraid: { source: "google", medium: "cpc" },
  gad_campaignid: { source: "google", medium: "cpc" },
  gad_source: { source: "google", medium: "cpc" },
  s_kwcid: { source: "google", medium: "cpc" },
  dclid: { source: "google", medium: "display" },
  srsltid: { source: "google", medium: "shopping" },
  msclkid: { source: "bing", medium: "cpc" },
  ttclid: { source: "tiktok", medium: "cpc" },
  fbclid: { source: "facebook", medium: "social" },
  twclid: { source: "twitter", medium: "social" },
  li_fat_id: { source: "linkedin", medium: "social" },
  epik: { source: "pinterest", medium: "social" },
  rdt_cid: { source: "reddit", medium: "cpc" },
  sccid: { source: "snapchat", medium: "cpc" },
  yclid: { source: "yandex", medium: "cpc" },
  mc_cid: { source: "mailchimp", medium: "email" },
  mkt_tok: { source: "marketo", medium: "email" },
  irclickid: { source: "impact", medium: "affiliate" },
  cjevent: { source: "cj", medium: "affiliate" },
  awc: { source: "awin", medium: "affiliate" },
}

/** Click-id param keys (lowercase), in lookup priority order. */
export const CLICK_ID_PARAMS: readonly string[] = Object.keys(CLICK_ID_CHANNEL)

/** Transactional/auth referrers — never acquisition sources. */
export const NON_ACQUISITION_REFERRER_HOSTS: readonly string[] = [
  "stripe.com",
  "paypal.com",
  "checkout.stripe.com",
]

/**
 * Resolves the first matching click id and its channel from lowercased params.
 *
 * @param {ReadonlyMap<string, string>} params - Lowercase query param map.
 * @returns {{ clickId?: string, clickIdType?: string, channel?: { source: string, medium: string } }} Match result.
 */
export function resolveClickIdFromParams(
  params: ReadonlyMap<string, string>,
): {
  clickId?: string
  clickIdType?: string
  channel?: { source: string; medium: string }
} {
  for (const key of CLICK_ID_PARAMS) {
    const value = params.get(key)
    if (value) {
      const channel = CLICK_ID_CHANNEL[key]
      if (channel) {
        return { clickId: value, clickIdType: key, channel }
      }
    }
  }
  return {}
}

/**
 * Whether a referrer host is transactional (Stripe/PayPal return), not acquisition.
 *
 * @param {string} host - Hostname without `www.`.
 * @returns {boolean} True when the referrer should not count as acquisition.
 */
export function isNonAcquisitionReferrerHost(host: string): boolean {
  return NON_ACQUISITION_REFERRER_HOSTS.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`),
  )
}

/** Search-engine referrer hosts → organic. Brand-prefix needles (trailing dot). */
const SEARCH_HOSTS: ReadonlyArray<readonly [string, string]> = [
  ["google.", "google"],
  ["bing.", "bing"],
  ["duckduckgo.", "duckduckgo"],
  ["yahoo.", "yahoo"],
  ["ecosia.", "ecosia"],
  ["baidu.", "baidu"],
  ["yandex.", "yandex"],
  ["brave.", "brave"],
]

/** Social referrer brand-prefix needles (trailing dot) → social. */
const SOCIAL_PREFIX_HOSTS: ReadonlyArray<readonly [string, string]> = [
  ["facebook.", "facebook"],
  ["instagram.", "instagram"],
  ["twitter.", "twitter"],
  ["linkedin.", "linkedin"],
  ["reddit.", "reddit"],
  ["youtube.", "youtube"],
  ["tiktok.", "tiktok"],
  ["pinterest.", "pinterest"],
  ["producthunt.", "producthunt"],
]

/** Social referrers that are exact hosts (short link / vanity domains). */
const SOCIAL_EXACT_HOSTS: ReadonlyArray<readonly [string, string]> = [
  ["t.co", "twitter"],
  ["x.com", "twitter"],
  ["lnkd.in", "linkedin"],
  ["youtu.be", "youtube"],
  ["news.ycombinator.com", "hackernews"],
]

function matchesPrefixNeedle(host: string, needle: string): boolean {
  return host.startsWith(needle) || host.includes(`.${needle}`)
}

/**
 * Maps an external referrer host to a `{ source, medium }` channel.
 *
 * @param {string} host - Referrer hostname without `www.`.
 * @returns {{ source: string, medium: string }} Classified channel.
 */
export function classifyReferrerHost(host: string): { source: string; medium: string } {
  for (const [needle, name] of SEARCH_HOSTS) {
    if (matchesPrefixNeedle(host, needle)) return { source: name, medium: "organic" }
  }
  for (const [needle, name] of SOCIAL_PREFIX_HOSTS) {
    if (matchesPrefixNeedle(host, needle)) return { source: name, medium: "social" }
  }
  for (const [needle, name] of SOCIAL_EXACT_HOSTS) {
    if (host === needle || host.endsWith(`.${needle}`)) return { source: name, medium: "social" }
  }
  return { source: host, medium: "referral" }
}
